import React, { useEffect, useRef, useState, FC } from 'react';
import { Helmet } from 'react-helmet-async';

import { BreakpointsInPx } from '../../../../../constants/breakpoints.constants';
import { handleThumbnailImage } from '../../../../../utils/youtubeUtils';
import { TContentHubImageProps } from '../../types/contentHubImage.types';

const ContentHubImage: FC<TContentHubImageProps> = ({
  desktopRendition,
  desktopSDRendition,
  tabletLandscapeRendition,
  tabletRendition,
  mobileRendition,
  alt,
  imageClassName,
  pictureClassName,
  imageStyle,
  pictureStyle,
  disableLazyLoad,
  prioritize,
  onClick,
  onTouchStart,
  onTouchEnd,
  onLoad,
  isVideoThumbnail,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (imageLoaded || !imageRef.current || !isVideoThumbnail) return;
    handleThumbnailImage(null, isVideoThumbnail, imageRef.current);
    setImageLoaded(() => true);
  }, [isVideoThumbnail, imageRef, isVideoThumbnail]);

  const getDefaultRenditionsAndBreakpoint = () => {
    let defaultRenditions = '';
    let defaultBreakpoint = '';

    if (desktopRendition?.url) {
      defaultRenditions = desktopRendition.url;
      defaultBreakpoint = BreakpointsInPx.xxl;
    } else if (desktopSDRendition?.url) {
      defaultRenditions = desktopSDRendition.url;
      defaultBreakpoint = BreakpointsInPx.xl;
    } else if (tabletRendition?.url) {
      defaultRenditions = tabletRendition.url;
      defaultBreakpoint = BreakpointsInPx.lg;
    } else if (tabletLandscapeRendition?.url) {
      defaultRenditions = tabletLandscapeRendition.url;
      defaultBreakpoint = BreakpointsInPx.md;
    } else if (mobileRendition?.url) {
      defaultRenditions = mobileRendition.url;
      defaultBreakpoint = BreakpointsInPx.sm;
    }
    return [defaultRenditions, defaultBreakpoint];
  };

  const [defaultRenditions, defaultBreakpoint] = getDefaultRenditionsAndBreakpoint();

  if (!defaultRenditions) {
    return null;
  }

  const handleOnLoad = (event) => {
    onLoad?.(event);
    handleThumbnailImage(event, isVideoThumbnail);
    setImageLoaded(() => true);
  };

  return (
    <>
      {prioritize && (
        <Helmet>
          {mobileRendition?.url && (
            <link
              rel="preload"
              href={mobileRendition?.url}
              as="image"
              media={defaultBreakpoint !== BreakpointsInPx.sm ? `(max-width: ${BreakpointsInPx.sm})` : '(max-width: none)'}
            />
          )}
          {tabletRendition?.url && (
            <link
              rel="preload"
              href={tabletRendition?.url}
              as="image"
              media={defaultBreakpoint !== BreakpointsInPx.md ? `(max-width: ${BreakpointsInPx.md})` : '(max-width: none)'}
            />
          )}
          {tabletLandscapeRendition?.url && (
            <link
              rel="preload"
              href={tabletLandscapeRendition?.url}
              as="image"
              media={defaultBreakpoint !== BreakpointsInPx.lg ? `(max-width: ${BreakpointsInPx.lg})` : '(max-width: none)'}
            />
          )}
          {desktopSDRendition?.url && (
            <link
              rel="preload"
              href={desktopSDRendition?.url}
              as="image"
              media={defaultBreakpoint !== BreakpointsInPx.xl ? `(max-width: ${BreakpointsInPx.xl})` : '(max-width: none)'}
            />
          )}
          {desktopRendition?.url && <link rel="preload" href={desktopRendition?.url} as="image" media="(max-width: none)" />}
        </Helmet>
      )}
      <picture className={pictureClassName} onClick={onClick} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} style={pictureStyle}>
        {mobileRendition?.url && (
          <source
            srcSet={mobileRendition.url}
            media={defaultBreakpoint !== BreakpointsInPx.sm ? `(max-width: ${BreakpointsInPx.sm})` : '(max-width: none)'}
            width={mobileRendition?.width}
            height={mobileRendition?.height}
          />
        )}
        {tabletRendition?.url && (
          <source
            srcSet={tabletRendition.url}
            media={defaultBreakpoint !== BreakpointsInPx.md ? `(max-width: ${BreakpointsInPx.md})` : '(max-width: none)'}
            width={tabletRendition?.width}
            height={tabletRendition?.height}
          />
        )}
        {tabletLandscapeRendition?.url && (
          <source
            srcSet={tabletLandscapeRendition.url}
            media={defaultBreakpoint !== BreakpointsInPx.lg ? `(max-width: ${BreakpointsInPx.lg})` : '(max-width: none)'}
            width={tabletLandscapeRendition?.width}
            height={tabletLandscapeRendition?.height}
          />
        )}
        {desktopSDRendition?.url && (
          <source
            srcSet={desktopSDRendition.url}
            media={defaultBreakpoint !== BreakpointsInPx.xl ? `(max-width: ${BreakpointsInPx.xl})` : '(max-width: none)'}
            width={desktopSDRendition?.width}
            height={desktopSDRendition?.height}
          />
        )}
        {desktopRendition?.url && (
          <source
            srcSet={desktopRendition.url}
            media="(max-width: none)"
            width={desktopRendition?.width}
            height={desktopRendition?.height}
          />
        )}
        <img
          loading={prioritize || disableLazyLoad ? 'eager' : 'lazy'}
          src={defaultRenditions}
          alt={alt || ''}
          className={imageClassName}
          style={imageStyle}
          onLoad={handleOnLoad}
          ref={imageRef}
        />
      </picture>
    </>
  );
};

export default ContentHubImage;
